/**
 * LOS COLORES DEL LOGO (MARCA-COLORES-UNA-FUENTE-T1 paso 4, Josse 17-oct, caso Farmavida). Sin DOM.
 *
 * El asistente de creacion preguntaba "color corporativo" a mano y lo guardaba aparte de la Apariencia; el fondo con IA
 * usa la Apariencia. Ahora es UNA sola fuente (nexo-api 552): el color de la marca es el color principal de la
 * Apariencia. Para elegirlo sin adivinar el #RRGGBB, el panel lee los pixeles del logo y propone sus colores
 * dominantes; el gotero toma el color exacto de un punto.
 *
 * @module application/negocios/coloresDelLogo
 */

const hex2 = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');

/** [r, g, b] -> '#rrggbb' */
export function aHex(r, g, b) {
  return '#' + hex2(r) + hex2(g) + hex2(b);
}

/** '#rrggbb' -> [r, g, b]; null si no es un color. */
export function deHex(h) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(h || '').trim());
  if (!m) return null;
  return [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
}

const distancia = (a, b) => Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2);

/**
 * Los colores dominantes de una imagen (los bytes RGBA de un canvas). Ignora lo transparente y el fondo casi blanco o
 * casi negro (un logo sobre fondo blanco no tiene "blanco" como color de marca). Agrupa por cubos de 32 niveles y
 * junta los que se parecen; devuelve hasta n en '#rrggbb', del mas presente al menos.
 * @param {Uint8ClampedArray|number[]} rgba
 * @param {number} n
 * @returns {string[]}
 */
export function coloresDominantes(rgba, n = 5) {
  const datos = rgba || [];
  const cubos = new Map();
  for (let i = 0; i + 3 < datos.length; i += 4) {
    const r = datos[i], g = datos[i + 1], b = datos[i + 2], a = datos[i + 3];
    if (a < 128) continue;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    if (min > 235 || max < 20) continue; // fondo blanco o negro
    const k = ((r >> 5) << 6) | ((g >> 5) << 3) | (b >> 5);
    const c = cubos.get(k) || { n: 0, r: 0, g: 0, b: 0 };
    c.n++; c.r += r; c.g += g; c.b += b;
    cubos.set(k, c);
  }
  const ordenados = [...cubos.values()].map((c) => ({ n: c.n, rgb: [c.r / c.n, c.g / c.n, c.b / c.n] })).sort((x, y) => y.n - x.n);
  const elegidos = [];
  for (const c of ordenados) {
    if (elegidos.some((e) => distancia(e.rgb, c.rgb) < 48)) continue;
    elegidos.push(c);
    if (elegidos.length >= n) break;
  }
  return elegidos.map((e) => aHex(...e.rgb));
}

/** El color de un pixel (gotero). x, y en pixeles de la imagen dibujada. null si cae fuera o es transparente. */
export function colorEnPunto(rgba, ancho, alto, x, y) {
  const xi = Math.floor(x), yi = Math.floor(y);
  if (xi < 0 || yi < 0 || xi >= ancho || yi >= alto) return null;
  const i = (yi * ancho + xi) * 4;
  if ((rgba[i + 3] ?? 0) < 128) return null;
  return aHex(rgba[i], rgba[i + 1], rgba[i + 2]);
}

/**
 * El color principal de la Apariencia en el alta: el de la marca si es un color valido; si no, el de Nexo.
 * Una sola fuente: el asistente ya no guarda dos colores distintos para el mismo negocio.
 */
export function colorPrincipalDelAlta(colorMarca, colorNexo) {
  const ok = deHex(colorMarca);
  return ok ? aHex(...ok) : colorNexo;
}
